import { FieldArray } from 'react-final-form-arrays';
import { useCallback, useState } from 'react';

import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { KnmLegalBases } from 'KnmPackage/MainModule/components/KnmLegalBases';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErpObjectResultSelectOption } from 'KnmErpPackage/MainModule/interfaces/IKnmErpObjectResultSelectOption';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';

import { IKnmErpFormViolation } from '../interfaces/IKnmErpFormViolation';
import styles from './KnmErpViolation.module.scss';

const COLLAPSE_TITLE_BASE = 'Нарушение';

interface IKnmErpViolationProps {
  formRef: IKnmFormRef | undefined;
  id: string;
  index: number;
  knmObjectsResultsSelectOptions: IKnmErpObjectResultSelectOption[] | undefined;
  name: string;
  value: IKnmErpFormViolation | undefined;
  watchOnly?: boolean;
  onLegalBasesOpenButtonClick: () => void;
  onRemove: () => void;
}

export const KnmErpViolation = ({
  formRef,
  id,
  index,
  knmObjectsResultsSelectOptions,
  name,
  value,
  watchOnly,
  onLegalBasesOpenButtonClick,
  onRemove,
}: IKnmErpViolationProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTrigger, setIsTrigger] = useState(false);
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const addInjunction = useCallback(() => !watchOnly && push && push(`${name}.injunctions`, undefined), [
    name,
    push,
    watchOnly,
  ]);
  const makeRemoveInjunctionHandler = (i: number) => () => !watchOnly && remove && remove(`${name}.injunctions`, i);

  const handleCollapseToggle = useCallback(() => {
    setIsOpen(!isOpen);
    setTimeout(() => {
      setIsTrigger(!isTrigger);
    });
  }, [isOpen, isTrigger]);

  return (
    <Row>
      <KnmCollapse
        id={id}
        theme="fields"
        title={value?.isNew ? COLLAPSE_TITLE_BASE : `${COLLAPSE_TITLE_BASE} №${index + 1}`}
        subtitle={value?.result?.label}
        isNew={value?.isNew}
        openByDefault={value?.isNew}
        opened={isOpen}
        onClose={onRemove}
        onToggle={handleCollapseToggle}
      >
        <>
          <Row>
            <FieldBlock label="Результат">
              <SelectInputField
                name={`${name}.result`}
                options={knmObjectsResultsSelectOptions}
                disabled={watchOnly}
                size="medium"
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Характер выявленного нарушения" color="black">
              <SelectInputFieldAutoOptions
                name={`${name}.type`}
                optionName={CommonCatalogsNamesEnum.KNM_VIOLATION_TYPES}
                disabled={watchOnly}
                size="medium"
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Сведения о выявленном нарушении" color="black">
              <TextareaField
                name={`${name}.note`}
                disabled={watchOnly}
                validate={makeRequired('Поле не может быть пустым')}
                triggerCalculateHeight={isTrigger}
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock
              label="Положение нарушенного правового акта"
              button={
                <Button
                  className="shared-btn-add"
                  text="Добавить"
                  variant="link"
                  color="primary"
                  type="button"
                  disabled={watchOnly}
                  onClick={onLegalBasesOpenButtonClick}
                />
              }
            />
          </Row>

          <Row>
            <KnmLegalBases className={styles.LegalBasesList} fieldName={`${name}.legalBases`} />
          </Row>

          <FieldBlock
            label="Сведения о выданных предписаниях об устранении выявленных нарушений и (или) о проведении мероприятий по
              предотвращению причинения вреда"
            button={
              <Button
                className="shared-btn-add"
                text="Добавить"
                variant="link"
                color="primary"
                type="button"
                disabled={watchOnly}
                onClick={addInjunction}
              />
            }
          >
            <FieldArray name={`${name}.injunctions`}>
              {({ fields }) => {
                if (!fields.length) {
                  return null;
                }

                return fields.map((injunctionsFieldName, injunctionIndex) => (
                  <Row isListItem margin="medium" key={injunctionsFieldName} notWrap>
                    <Row>
                      <Row>
                        <FieldBlock label={`Предписание №${injunctionIndex + 1}`} color="black" />
                      </Row>
                      <Row>
                        <FieldBlock label="Реквизиты предписания" color="black" placementLabel="left" labelSize="col-4">
                          <TextareaField
                            className="shared-row-field"
                            name={`${injunctionsFieldName}.code`}
                            disabled={watchOnly}
                            triggerCalculateHeight={isTrigger}
                          />
                        </FieldBlock>
                      </Row>
                      <Row>
                        <FieldBlock label="Дата вынесения предписания" color="black" placementLabel="left" size="half">
                          <DatePickerField
                            className={styles.DateField}
                            name={`${injunctionsFieldName}.appointmentDate`}
                            disabled={watchOnly}
                            minDate={new Date('01-01-2014')}
                          />
                        </FieldBlock>
                        <FieldBlock label="Срок выполнения предписания" color="black" placementLabel="left" size="half">
                          <DatePickerField
                            className="shared-row-field"
                            name={`${injunctionsFieldName}.executionDeadline`}
                            disabled={watchOnly}
                            minDate={new Date('01-01-2014')}
                          />
                        </FieldBlock>
                      </Row>
                      <FieldBlock label="Содержание предписания" color="black" placementLabel="left" labelSize="col-4">
                        <TextareaField
                          className="shared-row-field"
                          inputClassName={styles.InjunctionNoteInput}
                          name={`${injunctionsFieldName}.note`}
                          disabled={watchOnly}
                          triggerCalculateHeight={isTrigger}
                        />
                      </FieldBlock>
                    </Row>
                    <CloseButton
                      className="shared-deleting-row-btn"
                      onClick={makeRemoveInjunctionHandler(injunctionIndex)}
                    />
                  </Row>
                ));
              }}
            </FieldArray>
          </FieldBlock>
        </>
      </KnmCollapse>
    </Row>
  );
};
